import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {FlightMode} from "../../FlightMode";
import {FlightModeSymbol} from "../FlightModeSymbol";
import {FlightModeGameAssets} from "../../FlightModeGameAssets";
import {gsap} from "gsap";
import {TimelineSprite} from "@nolimitcity/promo-panel/bin/utils/TimelineSprite";
import {SpineGSAPWrapper} from "../../misc/SpineGSAPWrapper";

export class FlightModeReelView extends PIXI.Container implements StageLayer {

    private symbolWidth:number = 120;
    private symbolHeight:number = 148;

    private symbolMask:PIXI.Graphics;

    multiplierCounter:PIXI.Sprite;
    nextMultiplierText:PIXI.Text;
    actualMultiplier:PIXI.BitmapText;
    multiplier:number;
    multiplierSweep:PIXI.spine.Spine;

    avalanchedPositions:string[][] = [[], [], [], [], [], []];
    symbols:FlightModeSymbol[][] = [[],[],[],[],[],[]];
    symbolContainer:PIXI.Container;
    masklessSymbolContainer:PIXI.Container;

    landSounds:string[] = [];
    anticipationSounds:string[] = [
        "anticipationLastReelTop",
        "anticipationLastReelMiddle",
        "anticipationLastReelBottom",
    ];

    constructor() {
        super();
        this.name = "FlightModeReelView";

        // MULTIPLIER COUNTER
        this.multiplierCounter = new PIXI.Sprite(FlightMode.assetLoader.getTextures("Next_Multi_Panel")[0]);
        this.multiplierCounter.anchor.set(0.5, 0.5);
        this.multiplierCounter.position.set(500, 43)
        this.addChild(this.multiplierCounter);

        const textConfig = {
            fill: "#000033",
            fontFamily: "Helvetica",
            fontSize: 22,
            fontWeight: "bolder"
        };
        this.nextMultiplierText = new PIXI.Text(FlightMode.apiPlugin.translations.translate("NEXT MULTIPLIER"), textConfig);
        this.nextMultiplierText.anchor.set(0.5, 0.5);
        this.nextMultiplierText.position.set(428, 42);
        const multiScale = 230 / this.nextMultiplierText.width;
        if (multiScale < 1)
            this.nextMultiplierText.scale.set(multiScale, multiScale);
        this.addChild(this.nextMultiplierText);

        this.multiplier = 1;
        this.actualMultiplier = new PIXI.BitmapText("-", {
            fontName : FlightModeGameAssets.MULTIPLIER_FONT,
            fontSize : 45,
        });
        (<PIXI.Point>this.actualMultiplier.anchor).set(0.5, 0.5);
        this.actualMultiplier.position.set(628, 36);
        this.addChild(this.actualMultiplier);

        // SYMBOLS
        this.symbolContainer = new PIXI.Container();
        this.symbolContainer.sortableChildren = true;
        this.symbolContainer.position.set(0, 90);
        this.addChild(this.symbolContainer);

        this.symbolMask = new PIXI.Graphics();
        this.symbolMask.beginFill();
        this.symbolMask.drawRect(0, 0, 720, 591);
        this.symbolContainer.mask = this.symbolMask;
        this.symbolContainer.addChild(this.symbolMask);

        this.masklessSymbolContainer = new PIXI.Container();
        this.masklessSymbolContainer.sortableChildren = true;
        this.masklessSymbolContainer.position.set(0, 90);
        this.addChild(this.masklessSymbolContainer);

        this.multiplierSweep = new PIXI.spine.Spine(FlightMode.assetLoader.getSpineAsset(FlightModeGameAssets.MULTIPLIER_SWEEP))
        this.multiplierSweep.position.set(500, 43);
        this.addChild(this.multiplierSweep);

        this.resetLandSounds();

        this.setSymbols(FlightMode.model.reels, FlightMode.model.symbolSizes);
    }

    setSymbols(reels:string[][], sizes:number[][]) {
        this.getAllSymbols().forEach((sym) => this.symbolContainer.removeChild(sym));
        this.symbols = [[],[],[],[],[],[]];
        for (let reel = 0; reel < 6; reel++)
            for (let row = 0; row < 4; row++)
                if (reels[reel][row] != "AV") {
                    const newSymbol = new FlightModeSymbol(reels[reel][row],
                                                       sizes[reel][row]);
                    newSymbol.position.set((reel + 0.5) * this.symbolWidth,
                                           (row + 0.5) * this.symbolHeight)
                    this.symbols[reel].push(newSymbol);
                    this.symbolContainer.addChild(newSymbol);
                }
    }

    resetLandSounds() {
        this.landSounds = [];
        for (let i = 0; i < 18; i++)
            this.landSounds.push(`symLand${i+1}`);
    }

    getCurrentMultiplier() {
        return this.multiplier;
    }

    updateMultiplierCounter(extraMulti:number) {
        const newMulti = this.multiplier + extraMulti;
        const animation = (extraMulti == this.multiplier && this.multiplier > 1)
                          ? newMulti > 100 ? "FX_Cooler" : "FX_Cool"
                          : "FX" ;
        if (newMulti <= 1)
            return gsap.timeline();
        this.multiplier = newMulti;

        const hackjob = new PIXI.BitmapText(`${newMulti}X`, {
            fontName : FlightModeGameAssets.MULTIPLIER_FONT,
            fontSize : 45,
        });

        const baseScale = hackjob.width > 134 ? 134 / hackjob.width : 1;

        return gsap.timeline()
            .add(() => {
                FlightMode.soundPlayer.playSound("multiplierUpdate")
            })
            .add(SpineGSAPWrapper.createTimeline(this.multiplierSweep, 0, animation))
            .add(gsap.to(this.actualMultiplier.scale,
                {duration:0.15, x: baseScale * 1.2, y: baseScale * 1.2}), 0.1)
            .add(() => {
                this.actualMultiplier.text = `${newMulti}X`;
            }, 0.25)
            .add(gsap.to(this.actualMultiplier.scale, {duration:0.15, x: baseScale, y: baseScale}), 0.4)
    }

    doubleMultiplierCounter() {
        return this.updateMultiplierCounter(this.multiplier);
    }

    resetMultiplierCounter() {
        this.resetMultiplierCounterTo("-");
    }

    resetMultiplierCounterTo(target:number|string) {
        if (typeof target == "number") {
            this.multiplier = target;
            this.actualMultiplier.text = `${this.multiplier}X`;
        } else {
            this.multiplier = 1;
            this.actualMultiplier.text = "-";
        }
        const baseScale = this.actualMultiplier.width > 134 ? 134 / this.actualMultiplier.width : 1;
        this.actualMultiplier.scale.set(baseScale, baseScale);
    }

    moveUpSymbolAt(reel:number, row:number) {
        const symbol = this.symbols[reel][row];
        return () => {
            this.symbolContainer.removeChild(symbol);
            this.masklessSymbolContainer.addChild(symbol);
        }
    }

    moveDownSymbolAt(reel:number, row:number) {
        const symbol = this.symbols[reel][row];
        return () => {
            this.masklessSymbolContainer.removeChild(symbol);
            this.symbolContainer.addChild(symbol);
        }
    }

    moveUpSymbol(symbol:FlightModeSymbol) {
        return () => {
            this.symbolContainer.removeChild(symbol);
            this.masklessSymbolContainer.addChild(symbol);
        }
    }

    moveDownSymbol(symbol:FlightModeSymbol) {
        return () => {
            this.masklessSymbolContainer.removeChild(symbol);
            this.symbolContainer.addChild(symbol);
        }
    }

    getSymbolAt(reel:number, row:number) {
        return this.symbols[reel][row];
    }

    setSymbolAt(reel:number, row:number, symbol:FlightModeSymbol) {
        this.symbols[reel][row] = symbol;
    }

    getMaxWinSymbols() {
        return [0,1,2,3,4,5].map((reel) => this.symbols[reel][this.symbols[reel].length - 1]);
    }

    dropInSymbolsAtReel(reelIndex:number, symbols:FlightModeSymbol[], rowDelay = 0.024) {
        const symbolCount = this.symbols[reelIndex].length;
        const newSymbols = symbols.slice(0, 4 - symbolCount).reverse();
        const tl = gsap.timeline();
        let scatterCount = this.getAllSymbols().filter((s) => s.symbolName == "S").length;

        newSymbols.forEach((symbol, index) => {
            const targetIndex = 3 - index - symbolCount;
            const targetY = (targetIndex + 0.5) * this.symbolHeight;
            symbol.position.x = (reelIndex + 0.5) * this.symbolWidth;
            symbol.position.y = targetY - 444;
            this.symbolContainer.addChild(symbol);
            this.symbols[reelIndex].unshift(symbol);
            const landTl = gsap.timeline();
            landTl.add(gsap.to(symbol.position, {duration: 0.24, y: targetY, ease:"power3.in"}))
            landTl.add(this.moveUpSymbolAt(reelIndex, 0))
            landTl.add(symbol.landAnimation())
            //anticipation shake hackjob
            if (rowDelay > 0.024) {
                landTl.add(FlightMode.backgroundElements.shake(rowDelay == 0.72), '<');
                const sound = rowDelay == 0.72
                    ? this.anticipationSounds[targetIndex]
                    : this.landSounds.shift()!;
                landTl.add(() => {
                    FlightMode.soundPlayer.playSound(sound);
                }, '<')
            } else if (!FlightMode.model.isNotAvalanche()) {
                if ( ["S", 'W', "XH", "MI", "GM", "B"].indexOf(symbol.symbolName) == -1) {
                    const sound = this.landSounds.shift()!;
                    landTl.add(() => {
                        FlightMode.soundPlayer.playSound(sound);
                    }, '<')
                }
            }
            if (["S", 'W', "XH", "MI", "GM", "B"].indexOf(symbol.symbolName) > -1) {
                let sound;
                if (symbol.symbolName == "B")
                    sound = "bombLand";
                else if (symbol.symbolName == "MI")
                    sound = "multiplierIncreaserLand";
                else if (symbol.symbolName == "GM")
                    sound = "multiplierIncreaserGoldenLand";
                else if (symbol.symbolName == "W")
                    sound = "wildLand";
                else if (symbol.symbolName == "XH")
                    sound = "xHoleLand";
                else if (!(scatterCount == 0 && FlightMode.model.isBoostedBet()))
                    sound = `scatter${1 + scatterCount++}Land`;
                else
                    sound = ""
                landTl.add(() => {
                    FlightMode.soundPlayer.playSound(sound);
                }, '<')
            }
            landTl.add(this.moveDownSymbolAt(reelIndex, 0));
            tl.add(landTl, `<${rowDelay}`)
        })
        return tl;
    }

    dropInSymbols(symbols:FlightModeSymbol[][]) {
        const tl = gsap.timeline();
        const scatterThreshold = FlightMode.model.isLuckyDraw ? 2 : 1;
        let doneAnticipating = false;

        for (let i = 0; i < 6; i++) {
            const scatterCount = this.symbols
                .reduce((a,b) => a.concat(b), [])
                .filter((sym) => sym.symbolName == "S").length;
            const anticipation =
                scatterCount > scatterThreshold &&
                scatterCount < 5 &&
                !FlightMode.model.skipAnticipation;
            const justFinishedAnticipation = !doneAnticipating && scatterCount == 5;
            if (justFinishedAnticipation)
                doneAnticipating = true;

            const reelTl = this.dropInSymbolsAtReel(i, symbols[i], anticipation
                                                                   ? i == 5 ? 0.72 : 0.24
                                                                   : 0.024);
            if (!anticipation && FlightMode.model.isNotAvalanche()) {
                reelTl.add(() => {
                    FlightMode.soundPlayer.playSound(`symLand${(i % 2) + 1}`);
                }, '<0.24');
            }

            if (reelTl.duration() > 0)
                tl.add(reelTl, `${anticipation
                                  ? i == 5 ? '>-0.72' : '>-0.24'
                                  : justFinishedAnticipation ? ">" : '<0.03'}`);
            if (anticipation && i == 5 && reelTl.duration() > 0)
                tl.add(() => {}, '>0.72')
        }
        return tl;
    }

    dropInSymbolsInstant(symbols:FlightModeSymbol[][]) {
        const tl = gsap.timeline();
        for (let i = 0; i < 6; i++) {
            const reelTl = this.dropInSymbolsAtReel(i, symbols[i], 0);
            if (reelTl.duration() > 0)
                tl.add(reelTl, '<');
        }
        return tl;
    }

    dropOutSymbols(instant = false) {
        const tl = gsap.timeline();
        tl.add(() => {
            FlightMode.soundPlayer.playSound("symFallOut");
        })
        this.symbols.forEach((reel, reelIndex) => {
            const reelTl = gsap.timeline();
            reel.slice().reverse().forEach((symbol, rowIndex) => {
                if (rowIndex == 0)
                    return;
                const targetY = symbol.position.y + 592;
                reelTl.add(gsap.to(symbol.position, {duration: 0.24, y: targetY, ease:"power2.out"}),
                           `<${instant ? 0 : 0.024}`);
            })
            tl.add(reelTl, `<${instant ? 0 : 0.03}`);
        });
        for (let reel = 0; reel < 6; reel++) {
            const symbolsToRemove = this.symbols[reel].filter((sym) => sym.symbolName != "MAX");
            tl.add(() => {
                symbolsToRemove.forEach((sym) => this.symbolContainer.removeChild(sym));
            })
            this.symbols[reel] = this.symbols[reel].filter((sym) => sym.symbolName == "MAX");
        }

        return tl;
    }

    applyGravityAtReel(reel:number, avalancheData:boolean[]) {
        const tl = gsap.timeline();
        const reversedReel = this.symbols[reel].slice().reverse();
        const newReel =
            reversedReel.filter((symbol, index) => !avalancheData[3 - index]);

        if (newReel.length == 4)
            return tl;

        newReel.forEach((symbol, index) => {
            const oldIndex = reversedReel.indexOf(symbol);
            if (index != oldIndex) {
                const targetY = ((3 + 0.5 - index) * this.symbolHeight);
                const duration = 0.08 * (oldIndex - index);
                tl.add(gsap.to(symbol.position, {duration: duration, y: targetY, ease:"power2.in"}), '<')
            }
        });

        const symbolsToRemove = this.symbols[reel].filter((sym, index) => avalancheData[index]);
        tl.add(() => {
            symbolsToRemove.forEach((sym) => this.symbolContainer.removeChild(sym));
        }, '>0.1');
        this.symbols[reel] = this.symbols[reel].filter((sym, index) => !avalancheData[index]);

        return tl;
    }

    applyGravity(avalancheData:boolean[][]) {
        const tl = gsap.timeline();

        for (let reel = 0; reel < 6; reel++)
            tl.add(this.applyGravityAtReel(reel, avalancheData[reel]), '<');
        tl.add(() => {
            FlightMode.soundPlayer.playSound("symLand1");
        });
        return tl;
    }

    getAllSymbols() {
        return this.symbols.reduce((a,b) => a.concat(b.slice(0, 3)), []);
    }

    symbolCount() {
        return this.getAllSymbols().length;
    }
}